<template>
  <div class="homework-video">
    <video :id="playerId" class="video-js" playsinline></video>
  </div>
</template>

<script>
import axios from 'axios'
import videojs from 'video.js'
import 'videojs-contrib-hls'
import zhCN from 'video.js/dist/lang/zh-CN.json'

videojs.addLanguage('zh-CN', zhCN)

const activePlayers = new Set()

function pauseOtherPlayers(current) {
  activePlayers.forEach((player) => {
    if (player === current) return
    if (!player.isDisposed() && !player.paused()) player.pause()
  })
}

export default {
  name: 'HomeworkVideo',
  props: ['id', 'file'],
  data() {
    return {
      player: null,
      posterVideo: null,
      posterToken: 0
    }
  },
  computed: {
    playerId() {
      const raw = String(this.file || 'video')
      return `homework-video-${raw.replace(/[^a-zA-Z0-9_-]/g, '_')}`
    }
  },
  mounted() {
    this.loadVideo()
  },
  beforeDestroy() {
    this.stopPosterCapture()
    this.disposePlayer()
  },
  methods: {
    loadVideo() {
      const id = this.id
      if (!id) return
      axios.get(`${this.getDownloadUrl}${id}&type=5`).then((res) => {
        const data = res && res.data && res.data.data
        if (!data || !data.url || this._isDestroyed) return
        const isMp4 = String(data.ext || '').toLowerCase() === 'mp4'
        this.$nextTick(() => {
          if (this._isDestroyed) return
          this.initPlayer(data.url, isMp4)
        })
      }).catch((error) => {
        console.log(error)
      })
    },
    initPlayer(url, isMp4) {
      this.disposePlayer()
      this.player = videojs(this.playerId, {
        language: 'zh-CN',
        controls: true,
        autoplay: false,
        preload: 'metadata',
        inactivityTimeout: 0,
        playbackRates: [0.5, 1, 1.25, 1.5, 2],
        controlBar: {
          children: [
            'playToggle',
            'volumePanel',
            'progressControl',
            'playbackRateMenuButton',
            'fullscreenToggle'
          ]
        }
      })
      activePlayers.add(this.player)
      this.player.on('play', () => pauseOtherPlayers(this.player))
      this.player.src({
        src: url,
        type: isMp4 ? 'video/mp4' : 'application/x-mpegURL'
      })
      if (isMp4) this.capturePoster(url)
    },
    // 片头常是黑帧。用静音隐藏视频从第 1 秒往后截第一张有画面的帧，不进播放互斥
    capturePoster(url) {
      this.stopPosterCapture()
      const token = this.posterToken
      const video = document.createElement('video')
      this.posterVideo = video
      video.muted = true
      video.defaultMuted = true
      video.volume = 0
      video.playsInline = true
      video.preload = 'auto'
      video.crossOrigin = 'anonymous'
      video.style.cssText = 'position:fixed;width:2px;height:2px;left:-9999px;top:-9999px;opacity:0;pointer-events:none;'
      document.body.appendChild(video)

      const finish = () => {
        if (this.posterVideo === video) this.stopPosterCapture()
      }
      const seekAt = (time) => {
        if (token !== this.posterToken) return
        const duration = video.duration
        if (!duration || !isFinite(duration) || time >= duration - 0.05 || time > 30) {
          finish()
          return
        }
        let seekTimer = null
        const onSeeked = () => {
          clearTimeout(seekTimer)
          video.removeEventListener('seeked', onSeeked)
          if (token !== this.posterToken) return
          const poster = this.readPoster(video)
          if (poster) {
            if (poster !== 'tainted' && this.player && !this.player.isDisposed()) {
              this.player.poster(poster)
            }
            finish()
            return
          }
          seekAt(time + 1)
        }
        video.addEventListener('seeked', onSeeked)
        seekTimer = setTimeout(() => {
          video.removeEventListener('seeked', onSeeked)
          finish()
        }, 8000)
        try {
          video.currentTime = time
        } catch (e) {
          clearTimeout(seekTimer)
          finish()
        }
      }

      video.addEventListener('loadedmetadata', () => {
        const duration = video.duration || 0
        seekAt(duration > 1 ? 1 : 0)
      }, { once: true })
      video.addEventListener('error', () => finish(), { once: true })
      video.src = url
    },
    readPoster(video) {
      const vw = video.videoWidth
      const vh = video.videoHeight
      if (!vw || !vh) return null
      const canvas = document.createElement('canvas')
      const scale = Math.min(1, 640 / vw)
      canvas.width = Math.max(1, Math.round(vw * scale))
      canvas.height = Math.max(1, Math.round(vh * scale))
      const ctx = canvas.getContext('2d')
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        if (this.isNearlyBlack(ctx, canvas.width, canvas.height)) return null
        return canvas.toDataURL('image/jpeg', 0.72)
      } catch (e) {
        return 'tainted'
      }
    },
    isNearlyBlack(ctx, width, height) {
      const data = ctx.getImageData(0, 0, width, height).data
      const step = Math.max(4, Math.floor(data.length / 4 / 800) * 4)
      let sum = 0
      let count = 0
      for (let i = 0; i < data.length; i += step) {
        sum += data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
        count += 1
      }
      return count === 0 || sum / count < 16
    },
    stopPosterCapture() {
      this.posterToken += 1
      const video = this.posterVideo
      this.posterVideo = null
      if (!video) return
      video.pause()
      video.removeAttribute('src')
      video.load()
      if (video.parentNode) video.parentNode.removeChild(video)
    },
    disposePlayer() {
      if (!this.player) return
      activePlayers.delete(this.player)
      if (!this.player.isDisposed()) this.player.dispose()
      this.player = null
    }
  }
}
</script>

<style lang="less" scoped>
.homework-video {
  width: 100%;
  max-width: 640px;
  margin: 10px 0 16px;

  ::v-deep .video-js {
    width: 100%;
    height: 360px;
    background-color: #000;

    .vjs-poster {
      pointer-events: none;
      background-size: contain;
    }

    .vjs-big-play-button {
      z-index: 2;
    }

    .vjs-control-bar {
      display: flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
  }
}
</style>
